import { anonymousPuiApi } from '@client/api/waterlily';
import { useQuery } from '@tanstack/react-query';
import { useRequestPolling } from '@client/features/underwriting-eligibility/hooks/use-request-polling';
import { useMemo } from 'react';
import { formatIntakeForPUI } from '@shared';

interface AnonymizedIntakeSectionsResult {
  intakeSections?: FormattedIntakeSection[];
  partnerIntakeSections?: FormattedIntakeSection[];
  isLoading: boolean;
  error?: Error;
}

export function useAnonymizedIntakeSections(
  clientId: string,
  includePartner?: boolean,
): {
  data: AnonymizedIntakeSectionsResult | undefined;
  refetch: () => Promise<any>;
  isRefetchError: boolean;
} {
  // Fetch the request IDs
  const {
    data: requestIdsData,
    refetch,
    isError: isRefetchError,
  } = useQuery<AnonymousPuiRequestIdResponse>({
    queryKey: ['anonymousPuiDraft', clientId, includePartner],
    queryFn: () => anonymousPuiApi.fetchAnonymizationRequestId(clientId, !!includePartner),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: false,
  });

  // Poll for client anonymization results
  const {
    data: clientCompletionData,
    isPolling: isPollingClient,
    fetchingError: clientFetchError,
  } = useRequestPolling(requestIdsData?.clientAnonymizationRequestId || '', {
    maxPollingAttempts: 50,
    pollingIntervalMs: 2000,
  });

  // Poll for partner anonymization results (if applicable)
  const {
    data: partnerCompletionData,
    isPolling: isPollingPartner,
    fetchingError: partnerFetchError,
  } = useRequestPolling(requestIdsData?.partnerAnonymizationRequestId || '', {
    maxPollingAttempts: 50,
    pollingIntervalMs: 2000,
  });

  // Parse and format the results
  const formattedData = useMemo<AnonymizedIntakeSectionsResult | undefined>(() => {
    if (!requestIdsData) {
      return undefined;
    }

    const isLoading = isPollingClient || isPollingPartner;
    const error =
      clientFetchError || partnerFetchError
        ? new Error('Failed to fetch anonymization results')
        : undefined;

    if (
      !clientCompletionData ||
      clientCompletionData.status !== 'completed' ||
      !clientCompletionData.results
    ) {
      return {
        isLoading,
        error,
      };
    }

    let intakeSections: FormattedIntakeSection[] | undefined;
    try {
      const clientCitations = JSON.parse(clientCompletionData.results) as PrefillCitations[];
      intakeSections = formatIntakeForPUI(clientCitations);
    } catch (parseError) {
      return {
        isLoading,
        error: parseError as Error,
      };
    }

    let partnerIntakeSections: FormattedIntakeSection[] | undefined;
    if (requestIdsData.partnerAnonymizationRequestId) {
      if (
        !partnerCompletionData ||
        partnerCompletionData.status !== 'completed' ||
        !partnerCompletionData.results
      ) {
        return {
          intakeSections,
          isLoading,
          error,
        };
      }

      try {
        const partnerCitations = JSON.parse(partnerCompletionData.results) as PrefillCitations[];
        partnerIntakeSections = formatIntakeForPUI(partnerCitations);
      } catch (parseError) {
        return {
          intakeSections,
          isLoading,
          error: parseError as Error,
        };
      }
    }

    return {
      intakeSections,
      partnerIntakeSections,
      isLoading,
      error,
    };
  }, [
    requestIdsData,
    clientCompletionData,
    partnerCompletionData,
    isPollingClient,
    isPollingPartner,
    clientFetchError,
    partnerFetchError,
  ]);

  return {
    data: formattedData,
    refetch,
    isRefetchError,
  };
}
